import ClassicEditor from '@oracle/ortl-editor-classic/src/classiceditor.js';
import Alignment from '@oracle/ortl-alignment/src/alignment.js';
import Autoformat from '@oracle/ortl-autoformat/src/autoformat.js';
import Base64UploadAdapter from '@oracle/ortl-upload/src/adapters/base64uploadadapter.js';
import Bold from '@oracle/ortl-basic-styles/src/bold.js';
import Essentials from '@oracle/ortl-essentials/src/essentials.js';
import Font from '@oracle/ortl-font/src/font.js';
import Heading from '@oracle/ortl-heading/src/heading.js';
import HorizontalLine from '@oracle/ortl-horizontal-line/src/horizontalline.js';
import Image from '@oracle/ortl-image/src/image.js';
import ImageCaption from '@oracle/ortl-image/src/imagecaption.js';
import ImageResize from '@oracle/ortl-image/src/imageresize.js';
import ImageStyle from '@oracle/ortl-image/src/imagestyle.js';
import ImageToolbar from '@oracle/ortl-image/src/imagetoolbar.js';
import ImageInsert from '@oracle/ortl-image/src/imageinsert.js';
import Indent from '@oracle/ortl-indent/src/indent.js';
import IndentBlock from '@oracle/ortl-indent/src/indentblock.js';
import Italic from '@oracle/ortl-basic-styles/src/italic.js';
import Link from '@oracle/ortl-link/src/link.js';
import List from '@oracle/ortl-list/src/list.js';
import ListProperties from '@oracle/ortl-list/src/listproperties.js';
import Paragraph from '@oracle/ortl-paragraph/src/paragraph.js';
import PasteFromOffice from '@oracle/ortl-paste-from-office/src/pastefromoffice.js';
import RemoveFormat from '@oracle/ortl-remove-format/src/removeformat.js';
import Resize from './plugin/resize/src/resize.js';
import SourceEditing from './plugin/source-editing/src/sourceediting.js';
import Table from '@oracle/ortl-table/src/table.js';
import TableCellProperties from '@oracle/ortl-table/src/tablecellproperties.js';
import TableProperties from '@oracle/ortl-table/src/tableproperties.js';
import TableToolbar from '@oracle/ortl-table/src/tabletoolbar.js';
import TextTransformation from '@oracle/ortl-typing/src/texttransformation.js';
import Underline from '@oracle/ortl-basic-styles/src/underline.js';
import {GeneralHtmlSupport, HtmlComment} from '@oracle/ortl-html-support';
import UnifiedData from './plugin/unified-data/src/unifieddata.js';
import HandleEnter from './plugin/handle-enter/src/handleenter.js';
import PasteFromOfficeCleanup from './plugin/paste-from-office-cleanup/src/pastefromofficecleanup.js';
import XHtmlOutput from './plugin/xhtml-output/src/xhtmloutput.js';

class Editor extends ClassicEditor {}

// Plugins to include in the build.
Editor.builtinPlugins = [
	Alignment,
	Autoformat,
	Base64UploadAdapter,
	Bold,
	Essentials,
	Font,
	HandleEnter,
	Heading,
	HorizontalLine,
	HtmlComment,
	GeneralHtmlSupport,
	Image,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageInsert,
	Indent,
	IndentBlock,
	Italic,
	Link,
	List,
	ListProperties,
	Paragraph,
	PasteFromOffice,
	PasteFromOfficeCleanup,
	RemoveFormat,
	Resize,
	SourceEditing,
	Table,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextTransformation,
	Underline,
	UnifiedData,
	XHtmlOutput,
];

const COLOR_DEFINITION = {
	colors: [
		{
			color: 'rgb(0, 0, 0)',
			label: 'Black',
		},
		{
			color: 'rgb(77, 77, 77)',
			label: 'Dim grey',
		},
		{
			color: 'rgb(153, 153, 153)',
			label: 'Grey',
		},
		{
			color: 'rgb(230, 230, 230)',
			label: 'Light grey',
		},
		{
			color: 'rgb(255, 255, 255)',
			label: 'White',
			hasBorder: true,
		},
		{
			color: 'rgb(230, 76, 76)',
			label: 'Red',
		},
		{
			color: 'rgb(230, 153, 76)',
			label: 'Orange',
		},
		{
			color: 'rgb(230, 230, 76)',
			label: 'Yellow',
		},
		{
			color: 'rgb(153, 230, 76)',
			label: 'Light green',
		},
		{
			color: 'rgb(76, 230, 76)',
			label: 'Green',
		},
		{
			color: 'rgb(76, 230, 153)',
			label: 'Aquamarine',
		},
		{
			color: 'rgb(76, 230, 230)',
			label: 'Turquoise',
		},
		{
			color: 'rgb(76, 153, 230)',
			label: 'Light blue',
		},
		{
			color: 'rgb(76, 76, 230)',
			label: 'Blue',
		},
		{
			color: 'rgb(153, 76, 230)',
			label: 'Purple',
		},
	],
	columns: 5,
};

// Editor configuration.
Editor.defaultConfig = {
	toolbar: {
		items: [
			'sourceEditing',
			'|',
			'undo',
			'redo',
			'|',
			'bold',
			'italic',
			'underline',
			'|',
			'heading',
			'|',
			'link',
			'removeFormat',
			'alignment',
			'|',
			'bulletedList',
			'numberedList',
			'|',
			'outdent',
			'indent',
			'|',
			'insertImage',
			'insertTable',
			'horizontalLine',
			'|',
			'fontFamily',
			'fontSize',
			'fontColor',
			'fontBackgroundColor',
		],
		shouldNotGroupWhenFull: false,
	},
	language: 'en',
	image: {
		toolbar: ['imageTextAlternative', 'imageStyle:inline', 'imageStyle:block', 'imageStyle:side'],
	},
	table: {
		contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableCellProperties', 'tableProperties'],
	},
	resize: {
		minWidth: 150,
		minHeight: 85,
	},
	fontSize: {
		options: [8, 10, 12, 'default', 14, 18, 24, 36],
	},
	fontFamily: {
		options: [
			'default',
			'Arial',
			'Comic Sans MS',
			'Georgia',
			'MS Gothic',
			'Myriad Pro',
			'Open Sans',
			'Tahoma',
			'Times New Roman',
			'Trebuchet MS',
			'Verdana',
		],
	},
	fontBackgroundColor: COLOR_DEFINITION,
	fontColor: COLOR_DEFINITION,
};

export default Editor;
